import { useState } from "react";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";

import { auth } from "../FirebaseConfig";

/**
 * Sign-in screen. Direct `signInWithPopup` against Firebase Auth — no
 * server cookie round-trip, no Identity Toolkit exchange, no GIS
 * library. After a successful popup the top-level <AuthProvider>'s
 * onAuthStateChanged fires and the AccessGate swaps to the app.
 *
 * Works on mobile because Firebase Auth state lands in browser IndexedDB
 * on the same origin (firebaseapp.com); we never have to read the
 * credential out to send to a separate server.
 */
export function Login() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleGoogleSignIn() {
    setError(null);
    setBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      // Login is open to any Google account. Access to the app is
      // separately gated by hasAccess() (Usuarios.roles must include
      // "user", or the user must be admin), so creating an Auth
      // identity doesn't grant any privileges on its own.
      await signInWithPopup(auth, provider);
      // AuthProvider takes over and routes to NoAccess if the
      // Usuarios doc doesn't have the required role yet.
    } catch (err) {
      const e = err as { code?: string; message?: string };
      // User dismissed the popup — not an error worth showing.
      if (
        e.code === "auth/popup-closed-by-user" ||
        e.code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      setError(e.message ?? "No se pudo iniciar sesión con Google.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          AltaJet Vuelos
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Inicia sesión con tu cuenta Google para acceder.
        </p>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={busy}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
        >
          <GoogleIcon />
          {busy ? "Procesando…" : "Continuar con Google"}
        </button>
        <p className="mt-2 text-center text-xs italic text-slate-500">
          Cuenta Google personal o Workspace.
        </p>

        {error ? (
          <p className="mt-4 rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      />
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      />
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      />
    </svg>
  );
}
